import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/Reports.scss';
import '../styles/DashboardPage.scss';


const Reports = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [scores, setScores] = useState({});
  const [elementMaxPoints, setElementMaxPoints] = useState({});
  const [subMinFailed, setSubMinFailed] = useState({});

  useEffect(() => {
    api.get('/core/scorecard/list_elements/')
      .then((res) => setSources(
        res.data.map(({ key, label, elements }) => ({
          key,
          label,
          urls: elements.map((el) => `/core/scorecard/get_scorecard/?element=${el}`),
        }))
      ))
      .catch(() => {});
  }, []);

  useEffect(() => {
    sources.forEach(({ label, urls }) => {
      Promise.all(urls.map((url) => api.get(url).catch((err) => err.response ?? null)))
        .then((responses) => {
          const total = responses.reduce((sum, res) => sum + (res?.data?.total_points ?? 0), 0);
          const max = responses.reduce((sum, res) => {
            const inds = res?.data?.indicators || [];
            return sum + inds.reduce((s, ind) => s + (ind.points || 0), 0);
          }, 0);
          if (responses.some((r) => r?.data?.total_points != null)) {
            setScores((prev) => ({ ...prev, [label]: total }));
          }
          if (max > 0) {
            setElementMaxPoints((prev) => ({ ...prev, [label]: max }));
          }
          const failed = responses.some((r) =>
            (r?.data?.results || []).some((row) => row.sub_minimum_met === false)
          );
          setSubMinFailed((prev) => ({ ...prev, [label]: failed }));
        });
    });
  }, [sources]);

  const totalAchieved = sources.reduce(
    (sum, { label }) => sum + (scores[label] ?? 0),
    0
  );
  const hasAnyData = Object.keys(scores).length > 0;
  const anySubMinFailed = Object.values(subMinFailed).some(Boolean);

  const maxPoints = sources.reduce(
    (sum, { label }) => sum + (elementMaxPoints[label] ?? 0),
    0
  );

  const CARD_ICON = (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  );

  // Routes keyed by backend element key — labels come from the API
  const ROUTE_CONFIG = {
    ownership:                       { route: '/reports/ownership',                     desc: 'scorecard charts and KPI breakdown' },
    management_control:              { route: '/reports/scorecard',                     desc: 'scorecard charts and KPI breakdown' },
    skills_development:              { route: '/reports/skills-development',            desc: 'scorecard charts and KPI breakdown' },
    preferential_procurement:        { route: '/reports/preferential-procurement',      desc: 'scorecard charts and KPI breakdown' },
    enterprise_supplier_development: { route: '/reports/enterprise-development',        desc: 'scorecard charts and KPI breakdown' },
    sed:                             { route: '/reports/socio-economic-development',    desc: 'scorecard charts and KPI breakdown' },
  };

  const actions = [
    {
      label: 'Scenario Planning',
      hideScore: true,
      onClick: () => navigate('/reports/scenario'),
      desc: 'Model the points needed to reach a higher B-BBEE level — does not change your scorecard',
      icon: (
        <svg viewBox="0 0 24 24">
          <polyline points="3 17 9 11 13 15 21 7"/>
          <polyline points="14 7 21 7 21 14"/>
        </svg>
      ),
    },
    {
      label: 'Full B-BBEE Report',
      scoreKey: null,
      onClick: () => navigate('/reports/all'),
      desc: 'View and print all scorecards in a single combined report',
      icon: (
        <svg viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <line x1="16" y1="13" x2="8" y2="13"/>
          <line x1="16" y1="17" x2="8" y2="17"/>
          <line x1="12" y1="9" x2="12" y2="21"/>
        </svg>
      ),
    },

    ...sources.map(({ label, key }) => {
      const config = ROUTE_CONFIG[key] ?? {};
      return {
        label: `${label} Report`,
        scoreKey: label,
        onClick: () => navigate(config.route ?? '/reports'),
        desc: `View B-BBEE ${label} ${config.desc ?? ''}`,
        icon: CARD_ICON,
      };
    }),
  ];

  return (
    <div className="dp-container">
      <div className="dp-header">
        <button className="dp-back" onClick={() => navigate('/')}>← Back</button>
        <div className="dp-title">
          <h1>Reports &amp; KPI Tracking</h1>
          <p>B-BBEE Scorecard — Reports Element</p>
        </div>
      </div>

      <div className="dp-body">
        <div className="dp-section-label">Select a report to view</div>
        <div className="dp-grid">
          {actions.map((action) => {
            const achieved = action.scoreKey === null
              ? (hasAnyData ? totalAchieved : null)
              : scores[action.scoreKey] ?? null;
            const max = action.scoreKey === null
              ? maxPoints
              : (elementMaxPoints[action.scoreKey] ?? null);
            const showWarning = action.scoreKey === null
              ? anySubMinFailed
              : !!subMinFailed[action.scoreKey];
            return (
              <button key={action.label} className="dp-card" onClick={action.onClick}>
                <div className="dp-card-icon">{action.icon}</div>
                <span className="dp-card-label">{action.label}</span>
                <span className="dp-card-desc">{action.desc}</span>
                {!action.hideScore && (
                  <span className="dp-card-score-pill">
                    {achieved !== null ? achieved.toFixed(2) : '—'} / {max ?? '—'}
                  </span>
                )}
                {!action.hideScore && showWarning && (
                  <span className="dp-card-submin-badge">
                    ⚠
                    <span className="dp-card-submin-tooltip">
                      Sub-minimum thresholds not met — overall scorecard level will be discounted by one level.
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Reports;
